/**
 * ConceptBlock Component
 *
 * A reusable card for explaining musical concepts (Intervals, Chords, Progressions, etc.)
 * Features:
 * - Numbered badge with gradient
 * - Title and subtitle
 * - Rich content body
 * - Optional practice box
 * - Optional demo section
 * - Optional call-to-action link
 *
 * Used in: StartHere, Intervals Learn, Chords Learn, Progressions Learn
 */

import React from "react";
import { Link } from "react-router-dom";
import styles from "../../styles/learn/ConceptBlock.module.css";

interface PracticeBox {
    label: string;
    text: string;
}

interface ConceptBlockProps {
    number: number;
    title: string;
    subtitle: string;
    children: React.ReactNode;
    practiceBox?: PracticeBox;
    demo?: React.ReactNode;
    linkText?: string;
    linkTo?: string;
}

export default function ConceptBlock({
    number,
    title,
    subtitle,
    children,
    practiceBox,
    demo,
    linkText,
    linkTo,
}: ConceptBlockProps) {
    return (
        <div className={styles.concept}>
            <div className={styles.conceptHeader}>
                <div className={styles.conceptNumber}>{number}</div>
                <div className={styles.conceptTitleGroup}>
                    <h3 className={styles.conceptTitle}>{title}</h3>
                    <p className={styles.conceptSubtitle}>{subtitle}</p>
                </div>
            </div>

            <div className={styles.conceptBody}>
                {children}

                {practiceBox && (
                    <div className={styles.practiceBox}>
                        <p className={styles.practiceLabel}>{practiceBox.label}</p>
                        <p className={styles.practiceText}>{practiceBox.text}</p>
                    </div>
                )}

                {demo && <div className={styles.demoSection}>{demo}</div>}

                {linkTo && linkText && (
                    <Link to={linkTo} className={styles.conceptLink}>
                        {linkText}
                    </Link>
                )}
            </div>
        </div>
    );
}
